/**
 * Combobox for selecting insurance providers and plans, grouped by Organization.
 * @agentic @benchmark/core
 */

import { useState, useCallback } from "react";
import {
  Combobox,
  InputBase,
  Text,
  Stack,
  Group,
  Divider,
  Loader,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { IconBuilding, IconFileDescription } from "@tabler/icons-react";
import type { Organization, InsurancePlan } from "@medplum/fhirtypes";
import {
  useUnifiedInsuranceSearch,
  type InsuranceSearchResultGroup,
} from "@/hooks/shared/useUnifiedInsuranceSearch";
import { useDebounce } from "@benchmark/ui/hooks";

/**
 * Selected insurance organization and optional plan.
 * @agentic @benchmark/core
 */
export type InsuranceSelection = {
  type: "organization" | "plan";
  organizationId: string;
  organizationName: string;
  planId?: string;
  planName?: string;
};

/**
 * Props for InsuranceProviderCombobox component.
 * @agentic @benchmark/core
 */
type InsuranceProviderComboboxProps = {
  value?: InsuranceSelection | null;
  onChange?: (selection: InsuranceSelection | null) => void;
  disabled?: boolean;
  error?: string | boolean;
  required?: boolean;
  placeholder?: string;
};

/**
 * Renders async search combobox for insurance providers and plans.
 * @agentic @benchmark/core
 */
export const InsuranceProviderCombobox = ({
  value,
  onChange,
  disabled = false,
  error,
  required,
  placeholder = "Search insurance providers or plans...",
}: InsuranceProviderComboboxProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 300);

  const { data: searchResults, isLoading } = useUnifiedInsuranceSearch(
    searchQuery,
    { enabled: !disabled },
  );

  // Determine display value
  const displayValue = value
    ? value.planName
      ? `${value.organizationName} | ${value.planName}`
      : value.organizationName
    : "";

  // Handle organization selection
  const handleOrganizationSelect = useCallback(
    (org: Organization) => {
      onChange?.({
        type: "organization",
        organizationId: org.id!,
        organizationName: org.name || org.id!,
        planId: undefined,
        planName: undefined,
      });
      setSearchQuery("");
      combobox.closeDropdown();
    },
    [onChange, combobox],
  );

  // Handle plan selection
  const handlePlanSelect = useCallback(
    (plan: InsurancePlan, org: Organization) => {
      onChange?.({
        type: "plan",
        organizationId: org.id!,
        organizationName: org.name || org.id!,
        planId: plan.id!,
        planName: plan.name || plan.id!,
      });
      setSearchQuery("");
      combobox.closeDropdown();
    },
    [onChange, combobox],
  );

  // Handle clear
  const handleClear = useCallback(() => {
    onChange?.(null);
    setSearchQuery("");
  }, [onChange]);

  // UI state
  const hasMinChars = searchQuery.trim().length >= 2;
  const debouncedHasMinChars = debouncedQuery.trim().length >= 2;
  const isPendingSearch = hasMinChars && !debouncedHasMinChars && !isLoading;

  // Render group options
  // Note: Selection is handled by Combobox's onOptionSubmit, not onClick on each option.
  // Using both would cause handlers to fire twice.
  const renderGroupOptions = (group: InsuranceSearchResultGroup) => (
    <Stack key={group.organization.id} gap={0}>
      {/* Organization header - selectable */}
      <Combobox.Option value={`org:${group.organization.id}`}>
        <Group gap="xs">
          <IconBuilding size={16} />
          <Text fw={600}>{group.organization.name}</Text>
        </Group>
      </Combobox.Option>

      {/* Plans nested under organization */}
      {group.plans.length === 0 ? (
        <Text size="xs" c="dimmed" pl="xl" py={4}>
          No plans found
        </Text>
      ) : (
        group.plans.map((plan) => (
          <Combobox.Option key={plan.id} value={`plan:${plan.id}`} pl="xl">
            <Group gap="xs" wrap="nowrap">
              <IconFileDescription size={14} style={{ flexShrink: 0 }} />
              <Text size="sm" truncate>
                {plan.name}
              </Text>
            </Group>
          </Combobox.Option>
        ))
      )}

      <Divider my={4} />
    </Stack>
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        if (val.startsWith("org:")) {
          const orgId = val.replace("org:", "");
          const group = searchResults.find((g) => g.organization.id === orgId);
          if (group) handleOrganizationSelect(group.organization);
        } else if (val.startsWith("plan:")) {
          const planId = val.replace("plan:", "");
          for (const group of searchResults) {
            const plan = group.plans.find((p) => p.id === planId);
            if (plan) {
              handlePlanSelect(plan, group.organization);
              break;
            }
          }
        }
      }}
    >
      <Combobox.Target>
        <InputBase
          value={searchQuery || displayValue}
          onChange={(e) => {
            const newValue = e.currentTarget.value;
            // If the new value starts with displayValue and is longer, the user is typing over it
            // In this case, use the full new value as the search query
            // Otherwise, use the new value directly
            setSearchQuery(newValue);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => {
            if (searchQuery.length >= 2) combobox.openDropdown();
          }}
          onBlur={() => {
            combobox.closeDropdown();
            // Clear search query on blur to revert to display value if no selection was made
            // This ensures the input shows the actual selected value, not the search text
            setSearchQuery("");
          }}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          required={required}
          rightSection={
            isLoading ? (
              <Loader size={16} />
            ) : value ? (
              <Combobox.ClearButton onClear={handleClear} />
            ) : null
          }
          rightSectionPointerEvents={value ? "auto" : "none"}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <ScrollArea.Autosize mah={400} type="scroll">
          <Combobox.Options>
            {!hasMinChars ? (
              <Combobox.Empty>
                Type at least 2 characters to search
              </Combobox.Empty>
            ) : isPendingSearch || isLoading ? (
              <Combobox.Empty>Searching...</Combobox.Empty>
            ) : searchResults.length === 0 ? (
              <Combobox.Empty>No insurance providers found</Combobox.Empty>
            ) : (
              searchResults.map(renderGroupOptions)
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  );
};
